import * as cfe from 'ego-cfe';
import * as api from 'ego-sdk-js';
import React from 'react';

import useThreadRoot from './hooks/useThreadRoot';
import AiIcon from './icon/AiIcon';
import QnAIcon from './icon/QnAIcon';
import Button from './lib/Button';
import ToolTip from './lib/ToolTip';
import SectionHeaderTab from './SectionHeaderTab';
import Thread, { ThreadMethods, ThreadProps } from './Thread';

const ThreadQnA = (
  props: Omit<ThreadProps, 'headerOpts' | 'rootCommentOps' | 'viaEntryId'> & {
    viaEntry?: api.feed.IFeedEntryReference;
    aiAssist?: boolean;
  },
) => {
  const { viaEntry, ...rest } = props;
  const help = React.useMemo(
    () => (
      <p>
        This section is for questions and answers. If you'd like to blurt out a comment, first post a question that it
        could conceivably answer, and then post it as a reply.
      </p>
    ),
    [],
  );
  const rootCommentOpts = React.useMemo(
    () => ({
      help,
      maxLength: 127,
      simpleTextOnly: true,
      singleLine: true,
    }),
    [],
  );

  const threadRef = React.useRef<ThreadMethods | null>(null);
  const [threadCollapsed, setThreadCollapsed] = React.useState(false);
  const threadRoot = useThreadRoot(props.threadId);
  return (
    <div>
      <SectionHeaderTab
        className={props.showQuickCommentInput ? '!tw-mb-4' : undefined}
        title="Q & A"
        titleClassName={threadCollapsed ? 'tw-italic' : undefined}
        titleSuffix={
          threadRoot && cfe.ApiData.hasData(threadRoot) ? (
            <div className="tw-flex tw-items-center">
              <QnAIcon size="1rem" />
              <span className="tw-ml-1">{threadRoot.data.thread.comment_count}</span>
            </div>
          ) : undefined
        }
        round="right"
        right={
          <div className="tw-flex tw-gap-x-2 tw-items-center">
            {props.aiAssist ? (
              <ToolTip
                placement="left"
                overlay={
                  <div className="tw-w-40">
                    <p className="tw-mb-0">
                      When replying to a question, you can ask an AI with knowledge of the story for help. You can even
                      use it to answer your own questions.
                    </p>
                  </div>
                }
              >
                <span className="tw-text-primary tw-select-none tw-whitespace-nowrap tw-text-xs sm:tw-text-sm">
                  AI <span className="tw-hidden sm:tw-inline">Assist</span> <AiIcon size="1rem" offsetUp />
                </span>
              </ToolTip>
            ) : null}
            <Button sm className="tw-mr-4" onClick={() => threadRef.current?.showRootCommentForm()}>
              Ask Question
            </Button>
          </div>
        }
        onClick={() => {
          setThreadCollapsed(!threadCollapsed);
          if (threadRef.current) {
            threadRef.current.collapse(!threadCollapsed);
          }
        }}
      />
      <div className="tw-mb-2" />
      <div className="kb-pad-4-na">
        <Thread
          {...rest}
          key={props.threadId}
          ref={threadRef}
          addRootCommentTitle="Ask a Question"
          rootCommentOpts={rootCommentOpts}
          viaEntryId={viaEntry?.entry_id}
          enableAiPrompt={props.aiAssist}
        />
      </div>
    </div>
  );
};

export default ThreadQnA;
